import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';
import { api, unwrap } from '@/api/client';
import { Badge } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Field, RootError, Section, SecretInput, secretToSend } from '@/components/ui/form-controls';
import { Input } from '@/components/ui/input';
import { applyProblemToForm } from '@/lib/forms';
import { prettyJson } from '@/lib/json';
import { useJdeConfig, useJdeCredentials, useTenantMutation, type JdeVerifyResponse, type TenantDto } from './api';
import { JdeKeySection } from './JdeKeySection';
import { jdeCredentialsSchema, type JdeCredentialsForm } from './schemas';

/** jde-napi agent API account (03 §2): write-only secret, verify via /me, cached tenant config viewer. */
export function JdeTab({ tenant, canEditCredentials }: { tenant: TenantDto; canEditCredentials: boolean }) {
  const { t } = useTranslation();
  const configured = Boolean(tenant.jde.clientId);
  const [result, setResult] = useState<JdeVerifyResponse | null>(null);
  const config = useJdeConfig(tenant.id, configured);
  // ADR 0021: real jde-napi uses private_key_jwt; a tenant that already has a key stays in key mode.
  const creds = useJdeCredentials(tenant.id, canEditCredentials);
  const keyMode = creds.data ? creds.data.environmentMode === 'privateKeyJwt' || creds.data.mode === 'privateKeyJwt' : false;

  const form = useForm<JdeCredentialsForm>({
    resolver: zodResolver(jdeCredentialsSchema(t)),
    values: { clientId: tenant.jde.clientId ?? '', clientSecret: '' },
  });

  const save = useTenantMutation(tenant.id, async (v: JdeCredentialsForm) => {
    // The PUT verifies via jde /me before storing anything; the secret is write-only and never read back.
    return unwrap(
      await api.PUT('/v1/tenants/{id}/jde-credentials', {
        params: { path: { id: tenant.id } },
        body: { clientId: v.clientId.trim(), clientSecret: secretToSend(v.clientSecret) ?? '', keyId: null },
      }),
    );
  });
  const verify = useTenantMutation(tenant.id, async () =>
    unwrap(await api.POST('/v1/tenants/{id}/jde-credentials/verify', { params: { path: { id: tenant.id } } })),
  );

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      setResult(await save.mutateAsync(v));
      form.reset({ clientId: v.clientId, clientSecret: '' });
    } catch (err) {
      applyProblemToForm(form, err, { fields: ['clientId', 'clientSecret'] });
    }
  });
  const { errors, isSubmitting } = form.formState;
  const scopes = result?.scopes ?? tenant.jde.scopes;

  return (
    <div className="flex flex-col gap-8">
      <Section
        title={t('admin.jde.title')}
        description={t('admin.jde.description')}
        actions={
          configured && (
            <Button
              variant="outline"
              size="sm"
              disabled={verify.isPending}
              onClick={() =>
                void verify.mutateAsync(undefined).then(setResult, (err: unknown) =>
                  setResult({ ok: false, error: err instanceof Error ? err.message : String(err), scopes: [], jdeTenantId: null, tenantName: null, scopeSource: null }),
                )
              }
            >
              {t('admin.jde.verify')}
            </Button>
          )
        }
      >
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {tenant.jde.verifiedAt ? (
            <Badge variant="success">{t('admin.jde.verifiedAt', { at: new Date(tenant.jde.verifiedAt).toLocaleString() })}</Badge>
          ) : (
            <Badge variant="warning">{t('admin.jde.notVerified')}</Badge>
          )}
        </div>

        {canEditCredentials && keyMode ? null : canEditCredentials ? (
          <form className="grid max-w-xl gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
            <Field label={t('admin.jde.clientId')} htmlFor="jde-client" error={errors.clientId?.message}>
              <Input id="jde-client" className="font-mono" aria-invalid={!!errors.clientId} {...form.register('clientId')} />
            </Field>
            <Field label={t('admin.jde.clientSecret')} htmlFor="jde-secret" error={errors.clientSecret?.message}>
              <Controller
                control={form.control}
                name="clientSecret"
                render={({ field }) => (
                  <SecretInput id="jde-secret" configured={configured} value={field.value} onChange={field.onChange} invalid={!!errors.clientSecret} />
                )}
              />
            </Field>
            <RootError message={errors.root?.message} />
            <div>
              <Button type="submit" disabled={isSubmitting}>
                {t('admin.jde.saveAndVerify')}
              </Button>
            </div>
          </form>
        ) : (
          <p className="text-xs text-muted-foreground">{t('admin.jde.platformOnly')}</p>
        )}

        {result && (
          <div role="status" className="flex items-start gap-2 rounded-md border p-3 text-sm">
            {result.ok ? <CheckCircle2 className="size-4 text-success" /> : <XCircle className="size-4 text-destructive" />}
            <div>
              <div className="font-medium">{result.ok ? t('admin.jde.verifyOk', { name: result.tenantName ?? '' }) : t('admin.jde.verifyFailed')}</div>
              {result.error && <div className="text-xs text-destructive">{result.error}</div>}
            </div>
          </div>
        )}

        <div>
          <div className="mb-1 text-xs font-medium">{t('admin.jde.scopes')}</div>
          <div className="flex flex-wrap gap-1">
            {scopes.length === 0 && <span className="text-xs text-muted-foreground">{t('admin.jde.noScopes')}</span>}
            {scopes.map((s) => (
              <Badge key={s} className="font-mono">
                {s}
              </Badge>
            ))}
          </div>
        </div>
      </Section>

      {canEditCredentials && keyMode && <JdeKeySection tenant={tenant} onVerified={setResult} />}

      <Section
        title={t('admin.jde.configTitle')}
        description={t('admin.jde.configDescription')}
        actions={
          <Button variant="outline" size="sm" onClick={() => void config.refetch()} disabled={!configured || config.isFetching}>
            {t('admin.common.refresh')}
          </Button>
        }
      >
        {!configured && <p className="text-xs text-muted-foreground">{t('admin.jde.configNeedsCredentials')}</p>}
        {config.isError && <p className="text-xs text-destructive">{t('admin.jde.configError')}</p>}
        {config.data !== undefined && (
          <pre className="max-h-96 overflow-auto rounded-md border bg-muted/30 p-3 font-mono text-xs" data-testid="jde-config">
            {prettyJson(config.data)}
          </pre>
        )}
      </Section>
    </div>
  );
}
